import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  AdminTemplateRequestState,
  AdminTemplatesState,
  DefinicaoDeFinalidadeState,
  GestaoDeTemplatesWhatsappState,
  UsosDaTrocaDeFinalidadeState,
} from '../../types/adminTemplates';
import type { TemplateWhatsappDaGestao } from '../../types/templatesWhatsappGestao';
import {
  TEMPLATES_WHATSAPP_POR_PAGINA,
  USOS_DO_TEMPLATE_POR_PAGINA,
} from '../../utils/templatesWhatsappGestao';
import { infobipTemplateKey } from '../../utils/whatsappTemplates';
import {
  createAdminTemplate,
  createAndPublishWhatsappTemplate,
  definirFinalidadeDoTemplateWhatsapp,
  excluirTemplateWhatsapp,
  getAdminTemplateById,
  importInfobipTemplates,
  listarTemplatesWhatsappDaGestao,
  listarUsosDaTrocaDeFinalidade,
  listInfobipTemplates,
  listWhatsappTemplateLines,
  getWhatsappTemplateConfiguration,
  publishAdminTemplate,
  redistribuirTemplateWhatsapp,
  syncInfobipTemplateButtons,
  updateAdminTemplate,
} from './actions';

const idleRequest: AdminTemplateRequestState = { status: 'idle', error: null };

const definicaoOciosa: DefinicaoDeFinalidadeState = {
  ...idleRequest,
  templateId: null,
  conflito: null,
  erroDaDistribuicao: null,
};

const usosOciosos: UsosDaTrocaDeFinalidadeState = {
  ...idleRequest,
  items: [],
  total: 0,
  page: 0,
  limit: USOS_DO_TEMPLATE_POR_PAGINA,
  hasMore: false,
  requestId: null,
  templateId: null,
  finalidadeWhatsapp: null,
};

const gestaoWhatsappInicial: GestaoDeTemplatesWhatsappState = {
  lista: {
    ...idleRequest,
    items: [],
    total: 0,
    page: 1,
    limit: TEMPLATES_WHATSAPP_POR_PAGINA,
    hasMore: false,
    requestId: null,
  },
  definicao: definicaoOciosa,
  usosDaTroca: usosOciosos,
  redistribuicoes: {},
  exclusoes: {},
};

/** A linha atualizada fica onde estava, mesmo que saia do filtro: some só ao recarregar a lista. */
function substituirNaLista(
  gestao: GestaoDeTemplatesWhatsappState,
  template: TemplateWhatsappDaGestao,
): void {
  const indice = gestao.lista.items.findIndex((item) => item.id === template.id);
  if (indice !== -1) gestao.lista.items[indice] = template;
}

const initialState: AdminTemplatesState = {
  whatsappConfiguration: {
    ...idleRequest,
    podeEscolherOrigem: false,
    requestId: null,
  },
  whatsappLines: {
    ...idleRequest,
    capacidadeConfirmada: false,
    items: [],
    selecionada: null,
    total: 0,
    page: 0,
    limit: 20,
    hasMore: false,
    requestId: null,
    requestedPage: 1,
  },
  gestaoWhatsapp: gestaoWhatsappInicial,
  templates: [],
  currentTemplate: null,
  isLoading: false,
  error: null,
  whatsappCreation: idleRequest,
  infobipCatalog: { ...idleRequest, items: [] },
  infobipImport: idleRequest,
  infobipButtonSync: {},
};

const adminTemplatesSlice = createSlice({
  name: 'adminTemplates',
  initialState,
  reducers: {
    clearCurrentTemplate: (state) => {
      state.currentTemplate = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Sem limpar, a falha de uma tentativa anterior reapareceria ao reabrir o modal.
    resetWhatsappTemplateCreation: (state) => {
      state.whatsappCreation = idleRequest;
    },
    resetInfobipImport: (state) => {
      state.infobipImport = idleRequest;
      state.infobipButtonSync = {};
    },
    // Mesmo motivo do reset da criação: o conflito de um template não pode reaparecer no próximo.
    resetDefinicaoDeFinalidade: (state) => {
      state.gestaoWhatsapp.definicao = definicaoOciosa;
      state.gestaoWhatsapp.usosDaTroca = usosOciosos;
    },
    resetExclusaoTemplateWhatsapp: (state, action: PayloadAction<string>) => {
      // A tela pode reabrir durante o DELETE; a requisição continua dona da trava até terminar.
      if (state.gestaoWhatsapp.exclusoes[action.payload]?.status === 'loading') return;
      delete state.gestaoWhatsapp.exclusoes[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(excluirTemplateWhatsapp.pending, (state, action) => {
        state.gestaoWhatsapp.exclusoes[action.meta.arg] = {
          status: 'loading',
          error: null,
          requestId: action.meta.requestId,
        };
      })
      .addCase(excluirTemplateWhatsapp.fulfilled, (state, action) => {
        const exclusao = state.gestaoWhatsapp.exclusoes[action.meta.arg];
        if (exclusao?.requestId !== action.meta.requestId) return;
        exclusao.status = 'succeeded';
        exclusao.requestId = null;
      })
      .addCase(excluirTemplateWhatsapp.rejected, (state, action) => {
        const exclusao = state.gestaoWhatsapp.exclusoes[action.meta.arg];
        if (exclusao?.requestId !== action.meta.requestId) return;
        exclusao.status = 'failed';
        exclusao.requestId = null;
        exclusao.error =
          action.payload?.mensagem ??
          action.error.message ??
          'Não foi possível excluir o template.';
      })
      .addCase(getWhatsappTemplateConfiguration.pending, (state, action) => {
        state.whatsappConfiguration = {
          status: 'loading',
          error: null,
          podeEscolherOrigem: false,
          requestId: action.meta.requestId,
        };
      })
      .addCase(getWhatsappTemplateConfiguration.fulfilled, (state, action) => {
        if (state.whatsappConfiguration.requestId !== action.meta.requestId) return;
        state.whatsappConfiguration = {
          ...action.payload,
          status: 'succeeded',
          error: null,
          requestId: null,
        };
      })
      .addCase(getWhatsappTemplateConfiguration.rejected, (state, action) => {
        if (state.whatsappConfiguration.requestId !== action.meta.requestId) return;
        state.whatsappConfiguration.status = action.meta.aborted ? 'idle' : 'failed';
        state.whatsappConfiguration.error = action.meta.aborted
          ? null
          : (action.error.message ?? 'Não foi possível verificar a configuração de envio.');
        state.whatsappConfiguration.requestId = null;
      })
      .addCase(listWhatsappTemplateLines.pending, (state, action) => {
        state.whatsappLines.status = 'loading';
        state.whatsappLines.error = null;
        state.whatsappLines.requestId = action.meta.requestId;
        state.whatsappLines.requestedPage = action.meta.arg.page;
        if (action.meta.arg.page === 1) {
          state.whatsappLines.capacidadeConfirmada = false;
          state.whatsappLines.items = [];
          state.whatsappLines.selecionada = null;
          state.whatsappLines.page = 0;
          state.whatsappLines.hasMore = false;
          state.whatsappLines.total = 0;
        }
      })
      .addCase(listWhatsappTemplateLines.fulfilled, (state, action) => {
        if (state.whatsappLines.requestId !== action.meta.requestId) return;
        const anteriores = action.meta.arg.page > 1 ? state.whatsappLines.items : [];
        const items = [
          ...new Map(
            [...anteriores, ...action.payload.items].map((line) => [line.id, line]),
          ).values(),
        ];
        const selecionada =
          action.payload.selecionada ??
          (action.meta.arg.page > 1 ? state.whatsappLines.selecionada : null);
        state.whatsappLines = {
          ...action.payload,
          capacidadeConfirmada: true,
          items,
          selecionada,
          status: 'succeeded',
          error: null,
          requestId: null,
          requestedPage: action.payload.page,
        };
      })
      .addCase(listWhatsappTemplateLines.rejected, (state, action) => {
        if (state.whatsappLines.requestId !== action.meta.requestId) return;
        state.whatsappLines.status = action.meta.aborted ? 'idle' : 'failed';
        state.whatsappLines.error = action.meta.aborted
          ? null
          : (action.error.message ?? 'Não foi possível buscar as linhas de WhatsApp.');
        state.whatsappLines.requestId = null;
      })
      .addCase(listarTemplatesWhatsappDaGestao.pending, (state, action) => {
        const { lista } = state.gestaoWhatsapp;
        lista.status = 'loading';
        lista.error = null;
        lista.requestId = action.meta.requestId;
      })
      .addCase(listarTemplatesWhatsappDaGestao.fulfilled, (state, action) => {
        // A busca dispara uma consulta por pausa na digitação: só a última pode virar a tela.
        if (state.gestaoWhatsapp.lista.requestId !== action.meta.requestId) return;
        state.gestaoWhatsapp.lista = {
          ...action.payload,
          status: 'succeeded',
          error: null,
          requestId: null,
        };
      })
      .addCase(listarTemplatesWhatsappDaGestao.rejected, (state, action) => {
        const { lista } = state.gestaoWhatsapp;
        if (lista.requestId !== action.meta.requestId) return;
        lista.status = action.meta.aborted ? 'idle' : 'failed';
        lista.error = action.meta.aborted
          ? null
          : (action.error.message ?? 'Não foi possível listar os templates WhatsApp.');
        lista.requestId = null;
      })
      .addCase(definirFinalidadeDoTemplateWhatsapp.pending, (state, action) => {
        const { templateId } = action.meta.arg;
        state.gestaoWhatsapp.definicao = { ...definicaoOciosa, status: 'loading', templateId };
        // A gravação nova redistribui de novo: o resultado de um reenvio anterior já não vale.
        delete state.gestaoWhatsapp.redistribuicoes[templateId];
      })
      .addCase(definirFinalidadeDoTemplateWhatsapp.fulfilled, (state, action) => {
        state.gestaoWhatsapp.definicao = {
          ...definicaoOciosa,
          status: 'succeeded',
          templateId: action.meta.arg.templateId,
          erroDaDistribuicao: action.payload.erroDaDistribuicao,
        };
        substituirNaLista(state.gestaoWhatsapp, action.payload.template);
      })
      .addCase(definirFinalidadeDoTemplateWhatsapp.rejected, (state, action) => {
        const falha = action.payload;
        state.gestaoWhatsapp.definicao = {
          ...definicaoOciosa,
          status: 'failed',
          templateId: action.meta.arg.templateId,
          error:
            falha?.mensagem ?? action.error.message ?? 'Não foi possível definir a finalidade.',
          conflito:
            falha?.tipo === 'emUso'
              ? { usos: falha.usos, total: falha.total, truncado: falha.truncado }
              : null,
        };
      })
      .addCase(listarUsosDaTrocaDeFinalidade.pending, (state, action) => {
        const { templateId, finalidadeWhatsapp = null, page } = action.meta.arg;
        const anterior = state.gestaoWhatsapp.usosDaTroca;
        const continuaAMesmaTroca =
          page > 1 &&
          anterior.templateId === templateId &&
          anterior.finalidadeWhatsapp === finalidadeWhatsapp;
        state.gestaoWhatsapp.usosDaTroca = {
          ...(continuaAMesmaTroca ? anterior : usosOciosos),
          status: 'loading',
          error: null,
          requestId: action.meta.requestId,
          templateId,
          finalidadeWhatsapp,
        };
      })
      .addCase(listarUsosDaTrocaDeFinalidade.fulfilled, (state, action) => {
        const usos = state.gestaoWhatsapp.usosDaTroca;
        if (usos.requestId !== action.meta.requestId) return;
        const anteriores = action.meta.arg.page > 1 ? usos.items : [];
        state.gestaoWhatsapp.usosDaTroca = {
          ...usos,
          ...action.payload,
          items: [...anteriores, ...action.payload.items],
          status: 'succeeded',
          error: null,
          requestId: null,
        };
      })
      .addCase(listarUsosDaTrocaDeFinalidade.rejected, (state, action) => {
        const usos = state.gestaoWhatsapp.usosDaTroca;
        if (usos.requestId !== action.meta.requestId) return;
        usos.status = action.meta.aborted ? 'idle' : 'failed';
        usos.error = action.meta.aborted
          ? null
          : (action.error.message ?? 'Não foi possível verificar onde o template é usado.');
        usos.requestId = null;
      })
      .addCase(redistribuirTemplateWhatsapp.pending, (state, action) => {
        state.gestaoWhatsapp.redistribuicoes[action.meta.arg] = {
          status: 'loading',
          error: null,
          erroDaDistribuicao: null,
        };
      })
      .addCase(redistribuirTemplateWhatsapp.fulfilled, (state, action) => {
        const { gestaoWhatsapp } = state;
        const { erroDaDistribuicao, template } = action.payload;
        gestaoWhatsapp.redistribuicoes[action.meta.arg] = {
          status: 'succeeded',
          error: null,
          erroDaDistribuicao,
        };
        substituirNaLista(gestaoWhatsapp, template);
        // O diálogo de finalidade mostra a falha da distribuição que veio com a gravação; o
        // reenvio feito dali a substitui, senão o aviso antigo continuaria na tela.
        if (gestaoWhatsapp.definicao.templateId === action.meta.arg)
          gestaoWhatsapp.definicao.erroDaDistribuicao = erroDaDistribuicao;
      })
      .addCase(redistribuirTemplateWhatsapp.rejected, (state, action) => {
        state.gestaoWhatsapp.redistribuicoes[action.meta.arg] = {
          status: 'failed',
          error: action.error.message ?? 'Não foi possível reenviar o template às contas.',
          erroDaDistribuicao: null,
        };
      })
      .addCase(getAdminTemplateById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAdminTemplateById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTemplate = action.payload.data;
      })
      .addCase(getAdminTemplateById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Erro ao buscar template';
      })
      .addCase(createAdminTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAdminTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.templates.push(action.payload.data);
        state.currentTemplate = action.payload.data;
      })
      .addCase(createAdminTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Erro ao criar template';
      })
      .addCase(updateAdminTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAdminTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.templates.findIndex((t) => t.id === action.payload.data.id);
        if (index !== -1) {
          state.templates[index] = action.payload.data;
        }
        state.currentTemplate = action.payload.data;
      })
      .addCase(updateAdminTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Erro ao atualizar template';
      })
      .addCase(publishAdminTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(publishAdminTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.templates.findIndex((t) => t.id === action.payload.data.id);
        if (index !== -1) {
          state.templates[index] = action.payload.data;
        }
        state.currentTemplate = action.payload.data;
      })
      .addCase(publishAdminTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Erro ao publicar template';
      })
      .addCase(createAndPublishWhatsappTemplate.pending, (state) => {
        state.whatsappCreation = { status: 'loading', error: null };
      })
      .addCase(createAndPublishWhatsappTemplate.fulfilled, (state, action) => {
        state.whatsappCreation = { status: 'succeeded', error: null };
        state.currentTemplate = action.payload.data;
      })
      .addCase(createAndPublishWhatsappTemplate.rejected, (state, action) => {
        state.whatsappCreation = {
          status: 'failed',
          error: action.error.message || 'Erro ao criar template',
        };
      })
      .addCase(listInfobipTemplates.pending, (state) => {
        state.infobipCatalog = { status: 'loading', error: null, items: [] };
      })
      .addCase(listInfobipTemplates.fulfilled, (state, action) => {
        state.infobipCatalog = { status: 'succeeded', error: null, items: action.payload.data };
      })
      .addCase(listInfobipTemplates.rejected, (state, action) => {
        state.infobipCatalog = {
          status: 'failed',
          error: action.error.message || 'Erro ao buscar templates do Infobip',
          items: [],
        };
      })
      .addCase(importInfobipTemplates.pending, (state) => {
        state.infobipImport = { status: 'loading', error: null };
      })
      .addCase(importInfobipTemplates.fulfilled, (state) => {
        state.infobipImport = { status: 'succeeded', error: null };
      })
      .addCase(importInfobipTemplates.rejected, (state, action) => {
        state.infobipImport = {
          status: 'failed',
          error: action.error.message || 'Erro ao importar templates',
        };
      })
      .addCase(syncInfobipTemplateButtons.pending, (state, action) => {
        state.infobipButtonSync[infobipTemplateKey(action.meta.arg)] = {
          status: 'loading',
          error: null,
          quickReplyButtons: [],
        };
      })
      .addCase(syncInfobipTemplateButtons.fulfilled, (state, action) => {
        const { updated, templates } = action.payload.data;
        state.infobipButtonSync[infobipTemplateKey(action.meta.arg)] =
          updated > 0
            ? {
                status: 'succeeded',
                error: null,
                quickReplyButtons: templates[0]?.quickReplyButtons ?? [],
              }
            : {
                status: 'failed',
                error: 'Template não encontrado ou ainda não importado.',
                quickReplyButtons: [],
              };
      })
      .addCase(syncInfobipTemplateButtons.rejected, (state, action) => {
        state.infobipButtonSync[infobipTemplateKey(action.meta.arg)] = {
          status: 'failed',
          error: action.error.message || 'Erro ao atualizar botões do template',
          quickReplyButtons: [],
        };
      });
  },
});

export const {
  resetDefinicaoDeFinalidade,
  resetExclusaoTemplateWhatsapp,
  resetInfobipImport,
  resetWhatsappTemplateCreation,
} = adminTemplatesSlice.actions;
export default adminTemplatesSlice.reducer;
